<template>
  <Dialog
    :visible="visible"
    modal
    header="Submit Feedback"
    :style="{ width: '32rem' }"
    :closable="!isSubmitting"
    @update:visible="(val: boolean) => { if (!val) $emit('close') }"
  >
    <p class="feedback-dialog__intro">
      Whether you are reporting a bug or sharing an idea, it helps us to know:
    </p>
    <ul class="feedback-dialog__tips">
      <li><strong>Details:</strong> The specific page, URL, or workflow involved.</li>
      <li><strong>Context:</strong> A quick play-by-play of the issue, or the problem your feature request solves.</li>
      <li><strong>Visuals:</strong> Screenshots, recordings, or examples are always a huge help!</li>
    </ul>

    <div
      class="feedback-dialog__form"
      data-testid="feedback__form"
    >
      <Message
        v-if="submitError"
        severity="error"
        :closable="false"
        data-testid="feedback__submit-error"
      >
        {{ submitError }}
      </Message>
      <FieldGroup
        v-slot="{ inputId, describedBy, invalid }"
        label="Summary of Issue"
        for="feedback-summary"
        required
        :count="summary.length"
        :max="255"
        :error="summaryError"
      >
        <InputText
          :id="inputId"
          v-model="summary"
          :maxlength="255"
          :disabled="isSubmitting"
          :invalid="invalid"
          :pt="{ root: { 'aria-describedby': describedBy } }"
          placeholder="Brief summary of the issue or idea"
          fluid
        />
      </FieldGroup>

      <FieldGroup
        v-slot="{ inputId, describedBy, invalid }"
        label="Issue Description"
        for="feedback-description"
        required
        :error="descriptionError"
      >
        <Textarea
          :id="inputId"
          v-model="description"
          :maxlength="32000"
          :disabled="isSubmitting"
          :invalid="invalid"
          :pt="{ root: { 'aria-describedby': describedBy } }"
          auto-resize
          :rows="5"
          placeholder="Describe the issue or feature request in detail"
          fluid
        />
      </FieldGroup>

      <FieldGroup
        label="Attachments (optional)"
        for="feedback-attachments"
        :error="fileError"
      >
        <template #default="{ inputId, describedBy, invalid }">
          <FileUpload
            mode="basic"
            :multiple="true"
            :accept="acceptedTypes"
            :max-file-size="MAX_FILE_SIZE"
            :disabled="isSubmitting"
            :pt="{ input: { id: inputId, 'aria-describedby': describedBy, 'aria-invalid': invalid } }"
            choose-label="Choose Files"
            :auto="false"
            @select="onFileSelect"
          />
        </template>
        <template
          v-if="files.length > 0"
          #badge
        >
          <div class="feedback-dialog__files">
            <div
              v-for="(file, i) in files"
              :key="i"
              class="feedback-dialog__file"
            >
              <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
              <!-- per Studio 0007, icon controls repeat their name in a hover tooltip. -->
              <Button
                v-tooltip.top="'Remove attachment'"
                icon="pi pi-times"
                outlined
                aria-label="Remove attachment"
                severity="secondary"
                size="small"
                :disabled="isSubmitting"
                @click="removeFile(i)"
              />
            </div>
          </div>
        </template>
      </FieldGroup>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        text
        :disabled="isSubmitting"
        @click="$emit('close')"
      />
      <Button
        label="Submit"
        data-testid="feedback__submit"
        :loading="isSubmitting"
        :disabled="!canSubmit || isSubmitting"
        @click="handleSubmit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { FieldGroup } from '@neon-one/neon-circuit';
import type { FileUploadSelectEvent } from 'primevue/fileupload';

const props = defineProps<{
  visible: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitError?: string | null;
}>();

const emit = defineEmits<{
  close: [];
  submit: [{ summary: string; description: string; files: File[] }];
}>();

const acceptedTypes = 'image/*,.pdf,.txt,.log,.mp4,.mov';
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const summary = ref('');
const description = ref('');
const files = ref<File[]>([]);
const summaryError = ref('');
const descriptionError = ref('');
const fileError = ref('');

function onFileSelect(event: FileUploadSelectEvent) {
  const incoming = Array.from(event.files as File[]);

  if (files.value.length + incoming.length > MAX_FILES) {
    fileError.value = `Maximum ${MAX_FILES} attachments allowed`;
    return;
  }

  for (const f of incoming) {
    if (f.size > MAX_FILE_SIZE) {
      fileError.value = `File "${f.name}" exceeds the 10MB limit`;
      return;
    }
  }

  fileError.value = '';
  files.value.push(...incoming);
}

function removeFile(index: number) {
  files.value.splice(index, 1);
  fileError.value = '';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validate(): boolean {
  let valid = true;
  summaryError.value = '';
  descriptionError.value = '';

  if (!summary.value.trim()) {
    summaryError.value = 'Summary is required';
    valid = false;
  }
  if (!description.value.trim()) {
    descriptionError.value = 'Description is required';
    valid = false;
  }
  return valid;
}

function handleSubmit() {
  if (!validate()) return;
  emit('submit', {
    summary: summary.value.trim(),
    description: description.value.trim(),
    files: files.value,
  });
}

function resetForm() {
  summary.value = '';
  description.value = '';
  files.value = [];
  summaryError.value = '';
  descriptionError.value = '';
  fileError.value = '';
}

watch(
  () => props.visible,
  (visible) => {
    if (!visible && !props.isSubmitting) resetForm();
  },
);
</script>

<style scoped>
.feedback-dialog__intro {
  color: var(--app-text-muted);
  font-size: var(--app-font-size-body-base);
  margin: 0 0 var(--app-space-8) 0;
}

.feedback-dialog__tips {
  color: var(--app-text-muted);
  font-size: var(--app-font-size-body-small, 0.875rem);
  margin: 0 0 var(--app-space-20) 0;
  padding-left: var(--app-space-20);
}

.feedback-dialog__tips li {
  margin-bottom: var(--app-space-4);
}

.feedback-dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-16);
}

.feedback-dialog__files {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}

.feedback-dialog__file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--app-space-4) var(--app-space-8);
  background: var(--app-background-elevated);
  border-radius: var(--app-radius-sm);
  font-size: var(--app-font-size-caption);
  color: var(--app-text-primary);
}

</style>
